/*
 * Every TanStack Query key the desk uses, in one place. Nothing outside this
 * file writes a key array: add it here first, then use it from the hook.
 *
 * Each entry has an `all` root. Invalidating the root refreshes every variant
 * under it (`queryKeys.rates.all` → every property, room type and range), so
 * a new variant must start with its entry's root.
 *
 * Two keys must never share a root with different data shapes: a plain query
 * and an infinite query cached under the same key corrupt each other.
 */

export const queryKeys = {
  me: { all: ["me"] as const },

  /** The staff app. One root, so a preview refresh (kk:refresh) reloads the lot. */
  staff: {
    all: ["staff"] as const,
    me: ["staff", "me"] as const,
    device: ["staff", "device"] as const,
    join: (token: string) => ["staff", "join", token] as const,
    home: ["staff", "home"] as const,
    checks: ["staff", "checks"] as const,
    check: (id: string) => ["staff", "check", id] as const,
    places: ["staff", "places"] as const,
    repairs: ["staff", "repairs"] as const,
    repair: (id: string) => ["staff", "repair", id] as const,
    laundry: ["staff", "laundry"] as const,
    batch: (id: string) => ["staff", "batch", id] as const,
    keys: ["staff", "keys"] as const,
    lost: ["staff", "lost"] as const,
  },
  /** Settings › Users & roles › a staff member's access. */
  staffAccess: { all: ["staff-access"] as const, user: (id: string) => ["staff-access", id] as const },
  preview: { people: (propertyId: string) => ["preview-people", propertyId] as const },

  health: { all: ["health"] as const },

  /** Upkeep. Every variant starts with the root, so a submit can refresh the lot. */
  access: {
    all: ["access"] as const,
    list: (propertyId: string) => ["access", "list", propertyId] as const,
    history: (roomId: string) => ["access", "history", roomId] as const,
  },
  upkeep: {
    all: ["upkeep"] as const,
    plan: (propertyId: string, date: string) => ["upkeep", "plan", propertyId, date] as const,
    mine: (propertyId: string) => ["upkeep", "mine", propertyId] as const,
    rules: (propertyId: string) => ["upkeep", "rules", propertyId] as const,
    count: (scope: string) => ["upkeep", "count", scope] as const,
    room: (roomId: string, date: string) => ["upkeep", "room", roomId, date] as const,
    roomHistory: (roomId: string) => ["upkeep", "room-history", roomId] as const,
    task: (id: string) => ["upkeep", "task", id] as const,
    taskDetail: (id: string) => ["upkeep", "task-detail", id] as const,
    laundry: (propertyId: string) => ["upkeep", "laundry", propertyId] as const,
    batch: (id: string) => ["upkeep", "batch", id] as const,
    checklistTypes: (propertyId: string) => ["upkeep", "checklist-types", propertyId] as const,
    typeChecklist: (roomTypeId: string) => ["upkeep", "checklist", roomTypeId] as const,
    starters: ["upkeep", "starters"] as const,
    roomSetup: (roomId: string) => ["upkeep", "room-setup", roomId] as const,
  },

  dashboard: {
    all: ["dashboard"] as const,
    scope: (scope: string) => ["dashboard", scope] as const,
  },

  calendar: {
    all: ["calendar"] as const,
    range: (scope: string, from: string, to: string) => ["calendar", scope, from, to] as const,
  },

  /** Rooms free for a stay — the booking search. */
  search: {
    all: ["search"] as const,
    stay: (propertyId: string, checkIn: string, checkOut: string, adults?: number, beds?: number) =>
      ["search", propertyId, checkIn, checkOut, adults ?? 0, beds ?? 0] as const,
  },

  mealPlans: {
    all: ["meal-plans"] as const,
    property: (propertyId: string) => ["meal-plans", propertyId] as const,
  },

  addons: {
    all: ["addons"] as const,
    property: (propertyId: string) => ["addons", propertyId] as const,
  },

  team: {
    all: ["team"] as const,
    scope: (scope: string) => ["team", scope] as const,
  },

  booking: {
    all: ["booking"] as const,
    detail: (ref: string) => ["booking", ref] as const,
    history: (ref: string) => ["booking", ref, "history"] as const,
  },

  /** The command palette's booking lookup. */
  palette: {
    all: ["palette"] as const,
    search: (scope: string, term: string) => ["palette", scope, term] as const,
  },

  conflicts: {
    all: ["conflicts"] as const,
    list: (scope: string, status?: string) => ["conflicts", scope, status ?? "any"] as const,
    count: (scope: string) => ["conflicts", "count", scope] as const,
  },

  roomTypes: {
    all: ["room-types"] as const,
    property: (propertyId: string) => ["room-types", propertyId] as const,
  },

  rates: {
    all: ["rates"] as const,
    range: (propertyId: string, roomTypeId: string, from: string, to: string) => ["rates", propertyId, roomTypeId, from, to] as const,
  },

  guests: {
    all: ["guests"] as const,
    /** Filters, sort and page together: each combination is its own cached page. */
    list: (params: object) => ["guests", params] as const,
  },

  guest: {
    all: ["guest"] as const,
    detail: (id: string) => ["guest", id] as const,
  },

  /** A returning guest, found by phone while taking a booking. */
  guestLookup: {
    all: ["guest-lookup"] as const,
    phone: (phone: string) => ["guest-lookup", phone] as const,
  },

  users: {
    all: ["users"] as const,
    list: (params: object) => ["users", params] as const,
  },

  /** An invite link, read before the account exists. */
  invite: {
    all: ["invite"] as const,
    token: (token: string) => ["invite", token] as const,
  },

  /** How guests pay: the business's methods, and which a property takes. */
  paymentMethods: {
    all: ["payment-methods"] as const,
    of: (propertyId: string) => ["payment-methods", propertyId] as const,
  },
  /** The activity log, a page at a time. */
  activity: {
    all: ["activity"] as const,
    feed: (params: object) => ["activity", params] as const,
    areas: (params: object) => ["activity", "areas", params] as const,
  },

  settings: {
    all: ["settings"] as const,
    property: (propertyId: string) => ["settings", propertyId] as const,
  },

  /** Booking sources, business-wide. */
  channels: { all: ["channels"] as const },

  channelManager: {
    all: ["channel-manager"] as const,
    property: (propertyId: string) => ["channel-manager", propertyId] as const,
  },

  inventory: {
    all: ["inventory"] as const,
    property: (propertyId: string) => ["inventory", propertyId] as const,
  },

  /** Rooms of one property, for the Modify panel's room picker. */
  rooms: {
    all: ["rooms"] as const,
    property: (propertyId: string) => ["rooms", propertyId] as const,
  },

  locations: { all: ["locations"] as const },

  taxes: {
    all: ["taxes"] as const,
    property: (propertyId: string) => ["taxes", propertyId] as const,
  },

  /** Owner reports, one per kind and window. */
  reports: {
    all: ["reports"] as const,
    one: (kind: "performance" | "channels" | "cancellations", scope: string, from: string, to: string, ownership?: string) =>
      ["reports", kind, scope, from, to, ownership ?? "any"] as const,
  },

  /** The repair board, closed history, one issue, and the counts in the bar. */
  issues: {
    all: ["issues"] as const,
    open: (params: object) => ["issues", "open", params] as const,
    closed: (params: object) => ["issues", "closed", params] as const,
    counts: (scope: string) => ["issues", "counts", scope] as const,
    detail: (id: string) => ["issues", "detail", id] as const,
  },

  /** Spend: the table, its totals, one spend, and supplier names. */
  spends: {
    all: ["spends"] as const,
    list: (params: object) => ["spends", "list", params] as const,
    summary: (params: object) => ["spends", "summary", params] as const,
    detail: (id: string) => ["spends", "detail", id] as const,
    suppliers: (scope: string) => ["spends", "suppliers", scope] as const,
  },

  plans: {
    all: ["plans"] as const,
    scope: (scope: string) => ["plans", scope] as const,
  },

  handover: {
    all: ["handover"] as const,
    day: (scope: string, date: string) => ["handover", scope, date] as const,
  },

  lost: {
    all: ["lost"] as const,
    list: (params: object) => ["lost", "list", params] as const,
    detail: (id: string) => ["lost", "detail", id] as const,
  },
};
